import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import enFallbackJson from './locales/en.json';

const LanguageContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key) => key,
});

const STORAGE_KEY = 'app.language';
const FALLBACK_LANGUAGE = 'en';

const cachedDictionaries = {};

async function tryFetch(path) {
  try {
    const res = await fetch(path, { cache: 'no-cache' });
    if (!res.ok) return undefined;
    return await res.json();
  } catch {
    return undefined;
  }
}

async function loadDictionary(language) {
  if (cachedDictionaries[language]) return cachedDictionaries[language];
  // Try absolute then relative path
  const paths = [`/Locales/${language}.json`, `Locales/${language}.json`];
  for (const p of paths) {
    const data = await tryFetch(p);
    if (data) {
      cachedDictionaries[language] = data;
      return data;
    }
  }
  if (language === FALLBACK_LANGUAGE) {
    cachedDictionaries[language] = enFallbackJson || {};
    return cachedDictionaries[language];
  }
  throw new Error('Failed to load locale: ' + language);
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => localStorage.getItem(STORAGE_KEY) || FALLBACK_LANGUAGE);
  const [dict, setDict] = useState(enFallbackJson || {});
  const [fallbackDict, setFallbackDict] = useState(enFallbackJson || {});

  // Preload fallback dictionary once
  useEffect(() => {
    let active = true;
    loadDictionary(FALLBACK_LANGUAGE).then((d)=>{ if(active) setFallbackDict(d); }).catch(()=>{});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let isActive = true;
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.lang = language;
    loadDictionary(language)
      .then((data) => {
        if (isActive) setDict(data);
      })
      .catch(() => {
        if (isActive) setDict({});
      });
    return () => { isActive = false; };
  }, [language]);

  const t = useMemo(() => {
    return (key, fallback) => {
      const parts = key.split('.');
      const resolve = (root) => {
        let v = root;
        for (const part of parts) {
          if (v && Object.prototype.hasOwnProperty.call(v, part)) {
            v = v[part];
          } else {
            return undefined;
          }
        }
        return v;
      };
      let value = resolve(dict);
      if (value === undefined) value = resolve(fallbackDict);
      if (value !== undefined) return value;
      return fallback || key;
    };
  }, [dict, fallbackDict]);

  const setLanguageSafe = React.useCallback((nextLang) => {
    if (!nextLang) return;
    const normalized = String(nextLang).toLowerCase();
    const allowed = ['en','mr','hi'];
    if (!allowed.includes(normalized)) return;
    setLanguage((prev) => (prev === normalized ? prev : normalized));
  }, []);

  const contextValue = useMemo(() => ({ language, setLanguage: setLanguageSafe, t }), [language, setLanguageSafe, t]);

  return (
    <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);


